"use client";

import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslation } from "@/lib/i18n";
import LanguageToggle from "./LanguageToggle";
import ThemeToggle from "./ThemeToggle";
import { useTheme } from "@/lib/theme";
import { isDev } from "@/lib/featureFlags";

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isDevelopment, setIsDevelopment] = useState(false);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const pathname = usePathname();

  useEffect(() => {
    isDev().then(setIsDevelopment);
  }, []);

  const pageNavItems = [
    { href: "/blog", label: t("nav.blog") },
    // { href: "/customers", label: t("nav.customers") },
    { href: "/partners", label: t("nav.partners") },
    { href: "/ai-readiness", label: t("nav.aicheck") },
    { href: "/tech-radar", label: t("nav.techradar") },
    // { href: "/playbook", label: t("nav.playbook") },
  ];

  const linkClass = (href: string) =>
    `whitespace-nowrap transition-colors border-b pb-1 ${
      pathname === href
        ? "text-mint-text border-mint-text"
        : "hover:text-mint-text border-transparent hover:border-mint-text/50"
    }`;

  const mobileLinkClass = (href: string) =>
    `font-medium tracking-widest uppercase transition-colors ${pathname === href ? "text-mint-text" : "hover:text-mint-text"
    }`;

  const anchorLinkClass = (href: string) => {
    const hash = href.split("#")[1];
    const isActive = pathname === "/" && activeSection === hash;
    return `whitespace-nowrap transition-colors border-b pb-1 ${
      isActive
        ? "text-mint-text border-mint-text"
        : "hover:text-mint-text border-transparent hover:border-mint-text/50"
    }`;
  };

  const mobileAnchorLinkClass = (href: string) => {
    const hash = href.split("#")[1];
    const isActive = pathname === "/" && activeSection === hash;
    return `font-medium tracking-widest uppercase transition-colors ${isActive ? "text-mint-text" : "hover:text-mint-text"
      }`;
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Off the home page there are no sections to observe. The value is not
    // cleared here because every consumer already guards on `pathname === "/"`,
    // so clearing it would only cost an extra render.
    if (pathname !== "/") return;

    const sectionIds = ["solutions", "expertise", "projects", "contact"];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [pathname]);

  const navItems = [];

  navItems.push({ href: "/#solutions", label: t("nav.solutions") });

  if (isDevelopment) {
    navItems.push({ href: "/#projects", label: t("nav.projects") });
  }
  navItems.push({ href: "/#contact", label: t("nav.contact") });

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 border-b ${scrolled
        ? "bg-bg-nav backdrop-blur-md border-mint-text/20 py-3"
        : "bg-transparent border-transparent py-6"
        }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        {/* Logo */}
        <Link href="/#hero" className="flex items-center gap-2">
          {/* Full logo for desktop */}
          <Image
            src={theme === "dark" ? "/haintz-it-solutions/rectangle-white.svg" : "/haintz-it-solutions/rectangle.svg"}
            alt="HAINTZ IT-SOLUTIONS"
            width={240}
            height={50}
            className="hidden sm:block h-10 w-auto"
            priority
          />
          {/* Icon-only for mobile */}
          <Image
            src={theme === "dark" ? "/haintz-it-solutions/logo-white.svg" : "/haintz-it-solutions/logo.svg"}
            alt="HAINTZ IT-SOLUTIONS"
            width={40}
            height={40}
            className="sm:hidden h-8 w-auto"
            priority
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5 text-sm font-medium tracking-widest uppercase">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={anchorLinkClass(item.href)}
            >
              {item.label}
            </a>
          ))}
          {pageNavItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={linkClass(item.href)}
            >
              {item.label}
            </Link>
          ))}
          <ThemeToggle />
          <LanguageToggle />
          <Link
            href="/#contact"
            className="px-5 py-2 border border-mint-text/50 text-mint-text hover:bg-mint-text/10 transition-all text-xs font-bold tracking-widest"
          >
            {t("nav.cta")}
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex items-center gap-4 md:hidden">
          <ThemeToggle />
          <LanguageToggle />
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-bg-nav-mobile backdrop-blur-md border-t border-border-faint">
          <div className="px-6 py-8 flex flex-col gap-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-lg ${mobileAnchorLinkClass(item.href)}`}
              >
                {item.label}
              </a>
            ))}
            {pageNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsMenuOpen(false)}
                className={`text-lg ${mobileLinkClass(item.href)}`}
              >
                {item.label}
              </Link>
            ))}
            <a
              href="#contact"
              onClick={() => setIsMenuOpen(false)}
              className="mt-4 px-5 py-3 bg-mint text-black font-black tracking-widest text-center uppercase"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
